import React, { useState, useEffect } from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { useTheme } from '@mui/material/styles';
import api from '../utils/api';

/**
 * Renders LaTeX content using KaTeX library
 * @param {Object} props
 * @param {string} props.content - LaTeX content to render
 * @param {boolean} props.block - Whether to render as block or inline
 * @param {Object} props.options - KaTeX options
 */
const LatexRenderer = ({ content, block = false, options = {} }) => {
  if (!content) return null;

  // Define custom macros for common LaTeX commands
  const defaultOptions = {
    macros: {
      "\\R": "\\mathbb{R}",
      "\\Z": "\\mathbb{Z}",
      "\\N": "\\mathbb{N}",
      "\\Q": "\\mathbb{Q}",
      "\\C": "\\mathbb{C}",
      "\\dx": "\\,dx",
      "\\dy": "\\,dy",
      "\\dz": "\\,dz",
      "\\dt": "\\,dt",
      "\\dr": "\\,dr",
      "\\dtheta": "\\,d\\theta",
      ...options.macros
    },
    throwOnError: false, // Don't throw errors, show them inline
    errorColor: '#cc0000',
    ...options
  };

  try {
    return block ? (
      <BlockMath math={content} {...defaultOptions} />
    ) : (
      <InlineMath math={content} {...defaultOptions} />
    );
  } catch (error) {
    console.error('LaTeX rendering error:', error);
    return (
      <span style={{ color: '#cc0000', fontFamily: 'monospace' }}>
        {`LaTeX Error: ${content}`}
      </span>
    );
  }
};

/**
 * Component to render an image from \includegraphics command
 */
const LatexImage = ({ filename, width, documentId, imageMap, context = 'default' }) => {
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    const loadImage = async () => {
      try {
        let imageId = null;
        
        // Strip any extension from the filename for searching
        const baseFilename = filename.replace(/\.[^/.]+$/, '');
        
        
        // First check if we have an image map
        if (imageMap) {
          // Try exact match first
          if (imageMap[filename]) {
            imageId = imageMap[filename];
          } else {
            // Try to find with different extensions or no extension
            const possibleFilenames = [
              baseFilename,
              `${baseFilename}.jpg`,
              `${baseFilename}.jpeg`,
              `${baseFilename}.png`,
              `${baseFilename}.JPG`,
              `${baseFilename}.JPEG`,
              `${baseFilename}.PNG`
            ];
            
            for (const possibleName of possibleFilenames) {
              if (imageMap[possibleName]) {
                imageId = imageMap[possibleName];
                break;
              }
            }
          }
        }
        
        if (!imageId && documentId && documentId !== 'demo') {
          // If no image map match and we have a document ID (not demo), fetch the images
          const response = await api.get(`/database/documents/${documentId}/images`);
          const images = response.data;
          
          // Find the image by filename, trying different variations
          let image = images.find(img => img.filename === filename);
          
          if (!image) {
            // Try to match without extension or with different extensions
            image = images.find(img => {
              const imgBase = img.filename.replace(/\.[^/.]+$/, '');
              return imgBase === baseFilename || img.filename === baseFilename;
            });
          }
          
          if (image) {
            imageId = image.id;
          }
        }
        
        if (imageId) {
          // Check if this is a demo request
          if (documentId === 'demo') {
            // For demo images, use the demo API endpoint
            const imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/demo/images/${imageId}`;
            setImageData(imageUrl);
          } else {
            // Fetch the image data through the authenticated API
            const response = await api.get(`/database/images/${imageId}`, {
              responseType: 'blob'
            });
            
            // Create a blob URL from the response
            const imageBlob = new Blob([response.data], { type: response.headers['content-type'] });
            const imageUrl = URL.createObjectURL(imageBlob);
            setImageData(imageUrl);
          }
        } else {
          setError(`Image "${filename}" not found`);
        }
      } catch (err) {
        console.error('Error loading image:', err);
        setError(`Failed to load image: ${filename}`);
      } finally {
        setLoading(false);
      }
    };

    loadImage();
    
    // Cleanup blob URL on unmount
    return () => {
      if (imageData && imageData.startsWith('blob:')) {
        URL.revokeObjectURL(imageData);
      }
    };
  }, [filename, documentId, imageMap]);

  // Parse width (e.g., "0.8\textwidth" -> 80%) and apply context-based scaling
  const parseWidth = (w) => {
    let baseWidth = '100%';
    
    if (w) {
      if (w.includes('\\textwidth')) {
        const factor = parseFloat(w.replace('\\textwidth', ''));
        baseWidth = `${factor * 100}%`;
      } else if (w.includes('\\linewidth')) {
        const factor = parseFloat(w.replace('\\linewidth', ''));
        baseWidth = `${factor * 100}%`;
      } else {
        baseWidth = w;
      }
    }
    
    // Apply context-based scaling
    if (context === 'mcq-question') {
      // 30% smaller for MCQ question content
      if (baseWidth.endsWith('%')) {
        const percentage = parseFloat(baseWidth.replace('%', ''));
        return `${percentage * 0.7}%`;
      }
      return `calc(${baseWidth} * 0.7)`;
    } else if (context === 'mcq-option') {
      // 50% smaller for MCQ options
      if (baseWidth.endsWith('%')) {
        const percentage = parseFloat(baseWidth.replace('%', ''));
        return `${percentage * 0.5}%`;
      }
      return `calc(${baseWidth} * 0.5)`;
    }
    
    return baseWidth;
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>Loading image...</div>;
  }

  if (error) {
    return (
      <div style={{ 
        border: '1px dashed #ccc', 
        padding: '20px', 
        textAlign: 'center',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        margin: '10px 0'
      }}>
        <p style={{ color: '#666', margin: 0 }}>📷 {error}</p>
        <p style={{ fontSize: '12px', color: '#999', margin: '5px 0 0 0' }}>
          \includegraphics[width={width || ''}]{`{${filename}}`}
        </p>
      </div>
    );
  }

  return (
    <div style={{ 
      textAlign: 'center', 
      margin: '10px 0',
      overflow: 'hidden',
      width: '100%'
    }}>
      <img 
        src={imageData} 
        alt={filename}
        style={{ 
          maxWidth: '100%',
          width: parseWidth(width),
          height: 'auto',
          display: 'inline-block',
          objectFit: 'contain',
          // Apply invert filter for dark mode
          filter: theme.palette.mode === 'dark' ? 'invert(1) hue-rotate(180deg)' : 'none',
          // Smooth transition when switching themes
          transition: 'filter 0.3s ease'
        }}
        onError={() => setError(`Failed to load image: ${filename}`)}
      />
    </div>
  );
};

/**
 * Process a string that may contain LaTeX code and \includegraphics commands
 * @param {string} text - Text that may contain LaTeX delimited by $ or $$ and \includegraphics
 * @param {number} documentId - Document ID for loading images
 * @param {Object} imageMap - Optional pre-loaded mapping of filename to image ID
 * @param {string} context - Context for image sizing: 'default', 'mcq-question', 'mcq-option'
 * @returns {Array} Array of React elements with LaTeX and images rendered
 */
export const processLatexString = (text, documentId = null, imageMap = null, context = 'default') => {
  if (!text) return null;

  // Pre-process text to handle common LaTeX commands
  let processedText = text;
  
  // Convert \par to double line breaks
  processedText = processedText.replace(/\\par\s*/g, '\n\n');
  
  // Convert \medskip, \bigskip, \smallskip to line breaks
  processedText = processedText.replace(/\\(med|big|small)skip\s*/g, '\n\n');
  
  // Convert align environment to aligned for KaTeX
  processedText = processedText.replace(/\\begin\{align\}/g, '\\begin{aligned}');
  processedText = processedText.replace(/\\end\{align\}/g, '\\end{aligned}');
  
  // Convert align* environment to aligned for KaTeX
  processedText = processedText.replace(/\\begin\{align\*\}/g, '\\begin{aligned}');
  processedText = processedText.replace(/\\end\{align\*\}/g, '\\end{aligned}');
  
  // Handle other common LaTeX commands that KaTeX doesn't support
  processedText = processedText.replace(/\\noindent\s*/g, '');
  processedText = processedText.replace(/\\indent\s*/g, '    ');
  processedText = processedText.replace(/\\vspace\{[^}]*\}/g, '\n');
  processedText = processedText.replace(/\\hspace\{[^}]*\}/g, ' ');
  
  // Remove tcolorbox environments and their content
  // This matches: box{...}{ \begin{tcolorbox}... \end{tcolorbox} }
  processedText = processedText.replace(/box\{[^}]*\}\s*\{\s*\\begin\{tcolorbox\}[\s\S]*?\\end\{tcolorbox\}\s*\}/g, '');
  
  // Also remove standalone tcolorbox environments
  processedText = processedText.replace(/\\begin\{tcolorbox\}[\s\S]*?\\end\{tcolorbox\}/g, '');
  
  // Remove textcolor commands (keep the text content)
  processedText = processedText.replace(/\\textcolor\{[^}]*\}\{([^}]*)\}/g, '$1');
  
  // Handle font size commands (keep the content)
  processedText = processedText.replace(/\\(tiny|scriptsize|footnotesize|small|normalsize|large|Large|LARGE|huge|Huge)\s*/g, '');
  
  // Handle emphasis commands
  processedText = processedText.replace(/\\emph\{([^}]*)\}/g, '_$1_');
  processedText = processedText.replace(/\\textit\{([^}]*)\}/g, '_$1_');
  processedText = processedText.replace(/\\textbf\{([^}]*)\}/g, '**$1**');
  
  // Remove box commands that wrap content
  processedText = processedText.replace(/\\(f|m|par)?box\{[^}]*\}\{([^}]*)\}/g, '$2');

  // First, handle \includegraphics commands (before removing \textwidth)
  const includegraphicsRegex = /\\includegraphics(?:\[([^\]]*)\])?\{([^}]+)\}/g;
  
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = includegraphicsRegex.exec(processedText)) !== null) {
    // Add text before the image
    if (match.index > lastIndex) {
      parts.push(processedText.substring(lastIndex, match.index));
    }

    // Extract options and filename
    const options = match[1] || '';
    const filename = match[2];
    
    // Parse width from options
    let width = null;
    const widthMatch = options.match(/width\s*=\s*([^,\]]+)/);
    if (widthMatch) {
      width = widthMatch[1].trim();
    }

    // Add image component
    parts.push(
      <LatexImage 
        key={`img-${match.index}`}
        filename={filename}
        width={width}
        documentId={documentId}
        imageMap={imageMap}
        context={context}
      />
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < processedText.length) {
    parts.push(processedText.substring(lastIndex));
  }

  // If no images found, just use the original text
  if (parts.length === 0) {
    parts.push(processedText);
  }

  // Now process LaTeX in the text parts
  const processedParts = [];
  
  parts.forEach((part, partIndex) => {
    // Skip if it's already a React component (image)
    if (React.isValidElement(part)) {
      processedParts.push(part);
      return;
    }

    // Clean up any remaining unsupported commands in text parts only
    part = part.replace(/\\textwidth/g, '');
    part = part.replace(/\\linewidth/g, '');

    // Process LaTeX in text
    const blockRegex = /\$\$(.*?)\$\$/g;
    const inlineRegex = /\$(.*?)\$/g;

    // First, split by block math
    const blockParts = part.split(blockRegex);
    
    blockParts.forEach((blockPart, index) => {
      // Even indices are text (possibly with inline math)
      if (index % 2 === 0) {
        // Process inline math in text parts
        const inlineParts = blockPart.split(inlineRegex);
        
        inlineParts.forEach((inlinePart, inlineIndex) => {
          // Even indices are plain text
          if (inlineIndex % 2 === 0) {
            if (inlinePart) {
              // Only process line breaks that were explicitly added by LaTeX commands
              // Single newlines should be treated as spaces in LaTeX
              const processedInlinePart = inlinePart
                .replace(/([^\n])\n([^\n])/g, '$1 $2'); // Replace single newlines with spaces
              
              // Now split by double newlines (which came from \par, \medskip, etc.)
              const paragraphs = processedInlinePart.split(/\n\n+/);
              
              if (paragraphs.length === 1) {
                // No paragraph breaks, just add the text
                processedParts.push(
                  <React.Fragment key={`text-${partIndex}-${index}-${inlineIndex}`}>
                    {paragraphs[0]}
                  </React.Fragment>
                );
              } else {
                // Multiple paragraphs
                paragraphs.forEach((para, paraIndex) => {
                  if (para.trim()) {
                    processedParts.push(
                      <React.Fragment key={`para-${partIndex}-${index}-${inlineIndex}-${paraIndex}`}>
                        {para}
                      </React.Fragment>
                    );
                  }
                  
                  // Add paragraph break between paragraphs (but not after the last one)
                  if (paraIndex < paragraphs.length - 1 && para.trim()) {
                    processedParts.push(
                      <br key={`br-${partIndex}-${index}-${inlineIndex}-${paraIndex}`} />
                    );
                    processedParts.push(
                      <br key={`br2-${partIndex}-${index}-${inlineIndex}-${paraIndex}`} />
                    );
                  }
                });
              }
            }
          } else {
            // Odd indices are inline math
            processedParts.push(
              <LatexRenderer 
                key={`inline-${partIndex}-${index}-${inlineIndex}`} 
                content={inlinePart} 
                block={false} 
              />
            );
          }
        });
      } else {
        // Odd indices are block math
        processedParts.push(
          <LatexRenderer 
            key={`block-${partIndex}-${index}`} 
            content={blockPart} 
            block={true} 
          />
        );
      }
    });
  });

  // Wrap in a span to ensure inline math doesn't break flow
  return (
    <span style={{ lineHeight: '1.6' }}>
      {processedParts}
    </span>
  );
};

export default LatexRenderer;